import {
  FanguardApiContractsClientVenueRequestModel,
  FanguardApiContractsClientVenueSummaryModel,
} from 'src/api/generated';
import { get, patch, post, remove } from '@/api/api';
import { venuesQueries, VENUES_URI } from '@/api/queryKeys';
import { useQuery } from '@tanstack/react-query';
import { useMutationWithMessages } from './helpers';
import { AxiosError } from 'axios';
// import { useEffect } from 'react';
// import queryClient from 'src/queryClient';

const useGetVenues = (scopeClientId?: string) => {
  const isScoped = Boolean(scopeClientId?.length);
  const clientId = scopeClientId ?? '';

  const retry = (failureCount: number, error: unknown) => {
    if (error instanceof AxiosError && error.response?.status === 403) {
      return false;
    }
    return failureCount < 3;
  };

  return useQuery<FanguardApiContractsClientVenueSummaryModel[]>({
    queryKey: isScoped ? [...venuesQueries.list.queryKey, clientId] : venuesQueries.list.queryKey,
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientVenueSummaryModel[]>({
        uri: VENUES_URI,
        ...(isScoped ? { headers: { client: clientId, Client: clientId } } : {}),
      });
      return response.data;
    },
    retry,
  });
};

const useGetVenue = (id?: string) => {
  return useQuery({
    ...venuesQueries.detail(id ?? ''),
    enabled: !!id,
  });
};

const useCreateVenue = () => {
  return useMutationWithMessages(
    (data: FanguardApiContractsClientVenueRequestModel) => post({ uri: VENUES_URI, data }),
    {
      entityType: 'venue',
      action: 'create',
      queryKeysToInvalidate: [venuesQueries.list._def],
    },
  );
};

const useUpdateVenue = (id: string) => {
  return useMutationWithMessages(
    (data: FanguardApiContractsClientVenueRequestModel) => patch({ uri: `${VENUES_URI}/${data.id}`, data }),
    {
      entityType: 'venue',
      action: 'update',
      queryKeysToInvalidate: [venuesQueries.list._def, venuesQueries.detail(id).queryKey],
    },
  );
};

const useUploadVenueImage = (id: string) => {
  return useMutationWithMessages(
    (data: FanguardApiContractsClientVenueRequestModel) => patch({ uri: `${VENUES_URI}/${data.id}`, data }),
    {
      entityType: 'image',
      action: 'upload',
      queryKeysToInvalidate: [venuesQueries.list._def, venuesQueries.detail(id).queryKey],
    },
  );
};

const useDeleteVenueImage = (id: string) => {
  return useMutationWithMessages(
    (data: FanguardApiContractsClientVenueRequestModel) => patch({ uri: `${VENUES_URI}/${data.id}`, data }),
    {
      entityType: 'image',
      action: 'delete',
      queryKeysToInvalidate: [venuesQueries.list._def, venuesQueries.detail(id).queryKey],
    },
  );
};

const useDeleteVenue = () => {
  return useMutationWithMessages(
    ({ venueId, force = false }: { venueId: string; force?: boolean }) =>
      remove({ uri: `${VENUES_URI}/${venueId}${force ? '?force=true' : ''}` }),
    {
      entityType: 'venue',
      action: 'delete',
      shouldSuppressErrorMessage: (error) =>
        error instanceof AxiosError &&
        error.response?.status === 409 &&
        !error.config?.url?.includes('force=true'),
      queryKeysToInvalidate: [venuesQueries.list._def],
    },
  );
};

export {
  useGetVenues,
  useGetVenue,
  useCreateVenue,
  useUpdateVenue,
  useDeleteVenue,
  useUploadVenueImage,
  useDeleteVenueImage,
};
